import PaymentMode from '@/shared/enums/PaymentMode'

type ResolvePaymentMethodsInput = {
  method?: string | PaymentMode
  methods?: string | string[] | PaymentMode[]
  ignoreUnknownMethods?: boolean
}

const LEGACY_METHOD_ALIASES: Record<string, PaymentMode> = {
  DEBIT: PaymentMode.debitCard,
}

const PAYMENT_MODES = new Set(Object.values(PaymentMode))

function parseMethods(methods?: string | string[] | PaymentMode[]): string[] | null {
  if (methods === undefined || methods === null) {
    return null
  }

  if (Array.isArray(methods)) {
    const stringArray = (methods as Array<string | PaymentMode>).map(entry => String(entry))
    return stringArray.filter(entry => entry.trim() !== '')
  }

  if (typeof methods === 'string') {
    const trimmed = methods.trim()

    if (!trimmed) {
      return []
    }

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed)

        if (Array.isArray(parsed)) {
          return parsed.map(entry => String(entry)).filter(entry => entry.trim() !== '')
        }
      } catch (error) {
        console.warn('Failed to parse methods as JSON array, falling back to comma split:', error)
      }
    }

    if (trimmed.includes(',')) {
      return trimmed.split(',').map(entry => entry.trim()).filter(entry => entry !== '')
    }

    return [trimmed]
  }

  return [String(methods)]
}

export function normalizePaymentMethod(value: string | PaymentMode | undefined | null): PaymentMode | null {
  if (value === undefined || value === null) {
    return null
  }

  const method = String(value).trim().toUpperCase()

  if (!method) {
    return null
  }

  const alias = LEGACY_METHOD_ALIASES[method]
  const normalized = alias || method

  return PAYMENT_MODES.has(normalized as PaymentMode) ? normalized as PaymentMode : null
}

export function listAcceptedPaymentMethods(): string[] {
  const values = Array.from(PAYMENT_MODES)
  return Array.from(new Set([...values, ...Object.keys(LEGACY_METHOD_ALIASES)])).sort()
}

function isMethodProvided(method?: string | PaymentMode): boolean {
  return (
    method !== undefined &&
    method !== null &&
    String(method).trim() !== ''
  )
}

function noSupportedMethodError(unknownMethods: string[]): Error {
  return new Error(`No supported payment method remains after ignoring unknown payment method(s): ${unknownMethods.join(', ')}. Accepted values: ${listAcceptedPaymentMethods().join(', ')}`)
}

export function resolvePaymentMethods(input: ResolvePaymentMethodsInput): { methods: PaymentMode[], primaryMethod: PaymentMode } {
  const parsedMethods = parseMethods(input.methods)

  if (parsedMethods && parsedMethods.length > 0) {
    const invalidMethods: string[] = []
    const normalizedMethods: PaymentMode[] = []

    parsedMethods.forEach((method) => {
      const normalized = normalizePaymentMethod(method)

      if (!normalized) {
        invalidMethods.push(method)
      } else if (!normalizedMethods.includes(normalized)) {
        normalizedMethods.push(normalized)
      }
    })

    if (invalidMethods.length > 0) {
      if (!input.ignoreUnknownMethods) {
        throw new Error(`Invalid payment method(s): ${invalidMethods.join(', ')}. Accepted values: ${listAcceptedPaymentMethods().join(', ')}`)
      }

      if (normalizedMethods.length === 0) {
        throw noSupportedMethodError(invalidMethods)
      }

      console.warn(`Ignoring unknown payment method(s): ${invalidMethods.join(', ')}`)
    }

    if (normalizedMethods.length > 0) {
      return {
        methods: normalizedMethods,
        primaryMethod: normalizePaymentMethod(input.method) ?? normalizedMethods[0],
      }
    }
  }

  const fallbackMethod = normalizePaymentMethod(input.method)

  if (fallbackMethod) {
    return {
      methods: [fallbackMethod],
      primaryMethod: fallbackMethod,
    }
  }

  if (isMethodProvided(input.method)) {
    if (input.ignoreUnknownMethods) {
      throw noSupportedMethodError([String(input.method)])
    }

    throw new Error(`Invalid payment method: ${String(input.method)}. Accepted values: ${listAcceptedPaymentMethods().join(', ')}`)
  }

  throw new Error('Missing required prop: "method" or "methods"')
}
